import { Lock, Mail, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Field } from '@/components/common/FormBits'
import { useSendDraft, useSendQuote, type SendDraft } from '../../quotes'
import { toastError } from '../../ui'

const split = (v: string) =>
  v
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean)
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/** Onaylı teklifi e-postayla gönderme: gönderenin kendi posta kutusundan; tüm firma sahipleri CC'de (kaldırılamaz). */
export function SendQuoteModal({ quoteId, onClose }: { quoteId: string; onClose: () => void }) {
  const draft = useSendDraft(quoteId)
  if (!draft.data) {
    return (
      <Modal open size="lg" onOpenChange={(o) => !o && onClose()} icon={<Mail />} title="…">
        <Skeleton className="h-48" />
      </Modal>
    )
  }
  return <SendForm quoteId={quoteId} draft={draft.data} onClose={onClose} />
}

function SendForm({ quoteId, draft, onClose }: { quoteId: string; draft: SendDraft; onClose: () => void }) {
  const { t } = useTranslation()
  const send = useSendQuote()
  const [to, setTo] = useState(draft.to.join(', '))
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState(draft.subject)
  const [message, setMessage] = useState(draft.message)
  const [format, setFormat] = useState<'pdf' | 'docx'>('pdf')
  const toList = split(to)
  const ccList = split(cc)
  const bad = [...toList, ...ccList].some((e) => !EMAIL.test(e))
  const invalid = !toList.length || bad || !subject.trim() || !message.trim()

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(o) => !o && onClose()}
      icon={<Mail />}
      title={t('crm.quote.sendTitle')}
      description={t('crm.quote.sendHint')}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={invalid || send.isPending}
            onClick={() =>
              send.mutate(
                { id: quoteId, to: toList, cc: ccList, subject, message, format },
                {
                  onSuccess: () => {
                    toast.success(t('crm.quote.sent'))
                    onClose()
                  },
                  onError: toastError,
                },
              )
            }
          >
            <Send />
            {t('crm.quote.send')}
          </Button>
        </>
      }
    >
      {
        <div className="space-y-3">
          <Field id="sq-to" label={t('crm.quote.to')} error={bad ? t('crm.quote.badEmail') : undefined}>
            <Input id="sq-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="ad@musteri.com" />
          </Field>
          <div className="space-y-1">
            <p className="flex items-center gap-1.5 text-sm font-medium">
              <Lock className="size-3.5 text-muted-foreground" aria-hidden />
              {t('crm.quote.ownersCc')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {draft.ownersCc.length ? draft.ownersCc.map((e) => <Badge key={e} tone="neutral">{e}</Badge>) : <span className="text-xs text-muted-foreground">—</span>}
            </div>
            <p className="text-xs text-muted-foreground">{t('crm.quote.ownersCcHint')}</p>
          </div>
          <Field id="sq-cc" label={t('crm.quote.extraCc')}>
            <Input id="sq-cc" value={cc} onChange={(e) => setCc(e.target.value)} />
          </Field>
          <Field id="sq-subject" label={t('crm.quote.subject')}>
            <Input id="sq-subject" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field id="sq-message" label={t('crm.quote.message')}>
            <Textarea id="sq-message" rows={7} value={message} maxLength={10000} onChange={(e) => setMessage(e.target.value)} />
          </Field>
          <Field id="sq-format" label={t('crm.quote.attachment')}>
            <Select id="sq-format" value={format} onChange={(v) => setFormat(v as 'pdf' | 'docx')} options={[{ value: 'pdf', label: 'PDF' }, { value: 'docx', label: 'Word (.docx)' }]} />
          </Field>
        </div>
      }
    </Modal>
  )
}
